import React, { lazy } from 'react';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';
import { featureFlagsMap } from '@clatter/platform';

// avoid lazy loading ProtectedRoute to prevent delays in authentication checks (e.g., checkSession)
import ProtectedRoute from './components/HOC/ProtectedRoute';
import { AccessGuard, FeatureFlagGuard } from './components';
import { pagesMap } from './constants';

const HomePageView = lazy(() => import('./pages/Home/HomePageView'));
const RouteErrorPageView = lazy(() => import('./pages/RouteError/RouteErrorPageView'));
const EntityEditView = lazy(() => import('./pages/EntityEdit/EntityEditView'));
const AssetsManagerView = lazy(() => import('./pages/AssetsManager/AssetsManagerView'));
const ProjectMainPage = lazy(() => import('./pages/Projects/Stepper/MainPage/MainPage'));
const FolderFormView = lazy(() => import('./pages/FolderForm/FolderFormView'));
const LogoutView = lazy(() => import('./pages/Logout/LogoutView'));
const GroupsListView = lazy(() => import('./pages/Admin/Groups/GroupsListView'));
const GroupEditView = lazy(() => import('./pages/Admin/Groups/GroupEditView'));
const UsersListView = lazy(() => import('./pages/Admin/Users/UsersListView'));
const UserFormView = lazy(() => import('./pages/Admin/Users/UserFormView'));
const UsersLayout = lazy(() => import('./layouts/UsersLayout/UsersLayout'));
const SettingsView = lazy(() => import('./pages/Admin/Settings/SettingsView'));
const MetadataEditView = lazy(() => import('./pages/Admin/Settings/MetadataEditView'));
const MyWorkspaceView = lazy(() => import('./pages/MyWorkspace/MyWorkspaceView'));
const WorkspaceGQLPage = lazy(() => import('./pages/Workspaces/WorkspaceGQLPage'));
const WorkspaceTemplatesView = lazy(() => import('./pages/Workspaces/Templates/WorkspaceTemplatesListView'));
const WorkspaceTemplatesEditView = lazy(() => import('./pages/Workspaces/Templates/WorkspaceTemplateEditView'));
const ImpersonateView = lazy(() => import('./pages/Impersonate/ImpersonateView'));

const SlidespacesListView = lazy(() => import('./pages/Slidespaces/SlidespacesListView'));
const SlidespacesEditView = lazy(() => import('./pages/Slidespaces/SlidespacesEditView'));

const AssetManagerLayout = lazy(() => import('./layouts/AssetManagerLayout/AssetManagerLayout'));
const UserProfileLayout = lazy(() => import('./layouts/UserProfileLayout/UserProfileLayout'));
const UserProfileAboutMeView = lazy(() => import('./pages/UserProfile/AboutMeView'));
const UserProfileRolesView = lazy(() => import('./pages/UserProfile/RolesView'));
const UserProfileGroupView = lazy(() => import('./pages/UserProfile/GroupView'));
const UserProfileGrantsView = lazy(() => import('./pages/UserProfile/GrantsView'));

const EntitiesLayout = lazy(() => import('./layouts/EntitiesLayout/EntitesLayout'));
const EntitiesPage = lazy(() => import('./subPages/Entities/EntitiesPage'));
const DocumentsPage = lazy(() => import('./subPages/Documents/DocumentsPage'));

const ReportsLayout = lazy(() => import('./layouts/ReportsLayout/ReportsLayout'));
const DocumentsGeneratedView = lazy(() => import('./pages/Reports/DocumentsGenerated/DocumentsGeneratedView'));
const FilesDownloadedView = lazy(() => import('./pages/Reports/FilesDownloaded/FilesDownloadedView'));

const PresentationEditView = lazy(() => import('./pages/Presentations/EditPresentationView'));
const PresentationEditLayout = lazy(() => import('./layouts/PresentationEditLayout/PresentationEditLayout'));
const ConversationsView = lazy(() => import('./pages/Conversations/ConversationsView'));

const CategoriesEditView = lazy(() => import('./pages/Categories/EditCategoriesView'));

const CustomPage = lazy(() => import('./pages/CustomPage/CustomPageView'));

const PROJECTS_PATH = '/my-workspace/projects';

export const routesNames = {
  home: '/home',
  logout: `/logout`,

  createProject: `${PROJECTS_PATH}/create`,
  assets: (slug: string) => `/${slug}`,
  folders: (slug: string, folderId: string) => `/${slug}/folders/${folderId}`,
  createFolder: (slug: string, folderId: string) => `/${slug}/folders/${folderId}/create`, // slug -> rootFolder slug, folderId -> parent folderId
  entityEdit: (slug: string, entityType: string, entityId?: string) => `/${slug}/${entityType}/${entityId ?? ''}`,
  editProject: `${PROJECTS_PATH}/:projectId/:slug?`,
  manageUsers: '/users',
  createUser: '/users/create',
  editUser: (userId: string | number) => `/users/${userId}/edit`,
  groupsList: '/groups',
  createGroup: '/groups/create',
  settings: '/settings',
  createMetadata: '/settings/create',
  editMetadata: (key: string) => `/settings/${encodeURIComponent(key)}/edit`,
  workspaceGQL: `/workspaces/:id/graphql`,
  editGroup: (groupId: string) => `/groups/${groupId}/edit`,
  myWorkspace: (id?: string) => `my-workspace${id ? '/' + id : ''}`,

  workspaceTemplates: `${pagesMap.workspaceTemplates.slug}`,
  workspaceTemplatesEdit: `/${pagesMap.workspaceTemplates.slug}/:projectId/:slug?/:itemId?`,
  workspaceTemplateDocumentTemplateCreate: `/${pagesMap.workspaceTemplates.slug}/:projectId/:slug/create`,

  slidespaces: '/slidespaces',
  slidespacesEdit: '/slidespaces/:id/:slug?/:itemId?',
  slidespacesSlides: '/slidespaces/:id/:slug/folders/:folderId',
  editSlidespace: (slidespaceId: string | number) => `/slidespaces/${slidespaceId}/slides`,

  entities: '/entities',
  entitiesManage: 'manage',
  entitiesDataUploaded: 'data-uploaded',

  reports: '/reports',
  reportsDocumentsGenerated: 'documents-generated',
  reportsFilesDownloaded: 'files-downloaded',

  presentationsEdit: '/presentations/:presentationId',
  presentationsEditWithFolder: '/presentations/:presentationId/folders/:folderId?',
  presentationsEditSearch: '/presentations/:presentationId/search',

  categoriesEdit: `/${pagesMap.categories.slug}`,
  categoriesEditWithFolder: `/${pagesMap.categories.slug}/folders/:folderId?`,

  userProfile: '/profile',
  userProfileAboutMe: 'about-me',
  userProfileGroups: 'groups',
  userProfileRoles: 'roles',
  userProfileGrants: 'grants',

  impersonate: '/impersonate',

  conversationEdit: '/presentations/:presentationId/conversation',
};

const router = createBrowserRouter([
  {
    path: routesNames.logout,
    element: <LogoutView />,
    errorElement: <RouteErrorPageView />,
  },
  {
    path: '/',
    element: <AssetManagerLayout />,
    children: [
      {
        index: true,
        element: <Navigate to={routesNames.home} />,
      },
      {
        path: routesNames.home,
        element: <ProtectedRoute element={<HomePageView />} />,
      },
      {
        path: routesNames.userProfile,
        element: <ProtectedRoute element={<UserProfileLayout />} />,
        errorElement: <RouteErrorPageView />,
        children: [
          {
            path: routesNames.userProfileAboutMe,
            element: <UserProfileAboutMeView />,
          },
          {
            path: routesNames.userProfileRoles,
            element: <UserProfileRolesView />,
          },
          {
            path: routesNames.userProfileGroups,
            element: <UserProfileGroupView />,
          },
          {
            path: routesNames.userProfileGrants,
            element: <UserProfileGrantsView />,
          },
        ],
      },
      {
        path: routesNames.reports,
        element: (
          <ProtectedRoute
            element={
              <AccessGuard allowedRoles={pagesMap.reports.accessRoles}>
                <ReportsLayout />
              </AccessGuard>
            }
          />
        ),
        errorElement: <RouteErrorPageView />,
        children: [
          {
            index: true,
            element: <Navigate to={routesNames.reportsDocumentsGenerated} replace />,
          },
          {
            path: routesNames.reportsDocumentsGenerated,
            element: <DocumentsGeneratedView />,
          },
          {
            path: routesNames.reportsFilesDownloaded,
            element: <FilesDownloadedView />,
          },
        ],
      },
      {
        path: routesNames.presentationsEdit,
        element: <ProtectedRoute element={<PresentationEditLayout />} />,
        errorElement: <RouteErrorPageView />,
        children: [
          {
            index: true,
            element: <PresentationEditView />,
          },
          {
            path: 'folders/:folderId?',
            element: <PresentationEditView />,
          },
          {
            path: 'search',
            element: <PresentationEditView />,
          },
        ],
      },
      {
        path: routesNames.categoriesEdit,
        element: (
          <ProtectedRoute
            element={
              <AccessGuard allowedRoles={pagesMap.categories.accessRoles}>
                <CategoriesEditView />
              </AccessGuard>
            }
          />
        ),
      },
      {
        path: routesNames.categoriesEditWithFolder,
        element: (
          <ProtectedRoute
            element={
              <AccessGuard allowedRoles={pagesMap.categories.accessRoles}>
                <CategoriesEditView />
              </AccessGuard>
            }
          />
        ),
      },
      {
        path: routesNames.entities,
        element: (
          <ProtectedRoute
            element={
              <AccessGuard allowedRoles={pagesMap.entities.accessRoles}>
                <EntitiesLayout />
              </AccessGuard>
            }
          />
        ),
        errorElement: <RouteErrorPageView />,
        children: [
          {
            index: true,
            element: <Navigate to={routesNames.entitiesManage} replace />,
          },
          {
            path: routesNames.entitiesManage,
            element: <EntitiesPage isGlobal />,
          },
          {
            path: routesNames.entitiesDataUploaded,
            element: <DocumentsPage />,
          },
        ],
      },
      {
        path: routesNames.conversationEdit,
        element: (
          <ProtectedRoute
            element={
              <FeatureFlagGuard flagName={featureFlagsMap.NG.aiAssistant}>
                <ConversationsView />
              </FeatureFlagGuard>
            }
          />
        ),
        errorElement: <RouteErrorPageView />,
      },
      {
        path: routesNames.impersonate,
        element: <ProtectedRoute element={<ImpersonateView />} />,
        errorElement: <RouteErrorPageView />,
      },
      {
        path: routesNames.manageUsers,
        element: (
          <ProtectedRoute
            element={
              <AccessGuard allowedRoles={pagesMap.users.accessRoles}>
                <UsersLayout />
              </AccessGuard>
            }
          />
        ),
        errorElement: <RouteErrorPageView />,
        children: [
          {
            index: true,
            element: <UsersListView />,
          },
          {
            path: 'create',
            element: <UserFormView />,
          },
          {
            path: ':userId/edit',
            element: <UserFormView />,
          },
        ],
      },
      {
        path: routesNames.groupsList,
        element: (
          <ProtectedRoute
            element={
              <AccessGuard allowedRoles={pagesMap.groups.accessRoles}>
                <GroupsListView />
              </AccessGuard>
            }
          />
        ),
        errorElement: <RouteErrorPageView />,
      },
      {
        path: routesNames.settings,
        element: (
          <ProtectedRoute
            element={
              <AccessGuard allowedRoles={pagesMap.settings.accessRoles}>
                <SettingsView />
              </AccessGuard>
            }
          />
        ),
        errorElement: <RouteErrorPageView />,
      },
      {
        path: routesNames.createMetadata,
        element: (
          <ProtectedRoute
            element={
              <AccessGuard allowedRoles={pagesMap.settings.accessRoles}>
                <MetadataEditView />
              </AccessGuard>
            }
          />
        ),
        errorElement: <RouteErrorPageView />,
      },
      {
        path: '/settings/:metadataKey/edit',
        element: (
          <ProtectedRoute
            element={
              <AccessGuard allowedRoles={pagesMap.settings.accessRoles}>
                <MetadataEditView />
              </AccessGuard>
            }
          />
        ),
        errorElement: <RouteErrorPageView />,
      },
      {
        path: `/my-workspace/:id?`,
        element: <ProtectedRoute element={<MyWorkspaceView />} />,
        errorElement: <RouteErrorPageView />,
      },
      {
        path: routesNames.workspaceTemplates,
        element: (
          <ProtectedRoute
            element={
              <AccessGuard allowedRoles={pagesMap.workspaceTemplates.accessRoles}>
                <FeatureFlagGuard flagName={featureFlagsMap.NG.workspaces}>
                  <WorkspaceTemplatesView />
                </FeatureFlagGuard>
              </AccessGuard>
            }
          />
        ),
        errorElement: <RouteErrorPageView />,
      },
      {
        path: routesNames.workspaceTemplatesEdit,
        element: (
          <ProtectedRoute
            element={
              <AccessGuard allowedRoles={pagesMap.workspaceTemplates.accessRoles}>
                <FeatureFlagGuard flagName={featureFlagsMap.NG.workspaces}>
                  <WorkspaceTemplatesEditView />
                </FeatureFlagGuard>
              </AccessGuard>
            }
          />
        ),
        errorElement: <RouteErrorPageView />,
      },
      {
        path: routesNames.workspaceTemplateDocumentTemplateCreate,
        element: (
          <ProtectedRoute
            element={
              <AccessGuard allowedRoles={pagesMap.workspaceTemplates.accessRoles}>
                <FeatureFlagGuard flagName={featureFlagsMap.NG.workspaces}>
                  <WorkspaceTemplatesEditView />
                </FeatureFlagGuard>
              </AccessGuard>
            }
          />
        ),
        errorElement: <RouteErrorPageView />,
      },
      {
        path: routesNames.workspaceGQL,
        element: <ProtectedRoute element={<WorkspaceGQLPage />} />,
        errorElement: <RouteErrorPageView />,
      },
      {
        path: 'custom-page',
        handle: { appPagePadding: 'var(--top-nav-height, 60px) 0 0' },
        element: <ProtectedRoute element={<CustomPage />} />,
        errorElement: <RouteErrorPageView />,
      },
      {
        path: 'custom-page/:pageId',
        handle: { appPagePadding: 'var(--top-nav-height, 60px) 0 0' },
        element: <ProtectedRoute element={<CustomPage />} />,
        errorElement: <RouteErrorPageView />,
      },
      {
        path: routesNames.slidespaces,
        element: (
          <ProtectedRoute
            element={
              <AccessGuard allowedRoles={pagesMap.slidespaces.accessRoles}>
                <FeatureFlagGuard flagName={featureFlagsMap.NG.slidespaces}>
                  <SlidespacesListView />
                </FeatureFlagGuard>
              </AccessGuard>
            }
          />
        ),
        errorElement: <RouteErrorPageView />,
      },
      {
        path: '/slidespaces/:id',
        loader: () => {
          import('./pages/Slidespaces/components/SlidespaceCategoriesPage');
          import('./pages/Slidespaces/components/SlidespaceSettingsPage');
          import('./pages/Slidespaces/components/SlidespaceSlugPage');
          import('./pages/Slidespaces/components/SlidespaceIndexRedirect');
          return null;
        },
        element: (
          <ProtectedRoute
            element={
              <AccessGuard allowedRoles={pagesMap.slidespaces.accessRoles}>
                <FeatureFlagGuard flagName={featureFlagsMap.NG.slidespaces}>
                  <SlidespacesEditView />
                </FeatureFlagGuard>
              </AccessGuard>
            }
          />
        ),
        errorElement: <RouteErrorPageView />,
        children: [
          {
            index: true,
            lazy: async () => {
              const { SlidespaceIndexRedirect } = await import(
                './pages/Slidespaces/components/SlidespaceIndexRedirect'
              );
              return { Component: SlidespaceIndexRedirect };
            },
          },

          {
            path: 'categories',
            lazy: async () => {
              const { SlidespaceCategoriesPage } = await import(
                './pages/Slidespaces/components/SlidespaceCategoriesPage'
              );
              const Component = () => (
                <FeatureFlagGuard flagName={featureFlagsMap.NG.curatedCategories}>
                  <SlidespaceCategoriesPage />
                </FeatureFlagGuard>
              );
              return { Component };
            },
          },
          {
            path: 'settings',
            lazy: async () => {
              const { SlidespaceSettingsPage } = await import('./pages/Slidespaces/components/SlidespaceSettingsPage');
              return { Component: SlidespaceSettingsPage };
            },
          },
          {
            path: ':slug',
            lazy: async () => {
              const { SlidespaceSlugPage } = await import('./pages/Slidespaces/components/SlidespaceSlugPage');
              return { Component: SlidespaceSlugPage };
            },
          },
          {
            path: ':slug/folders/:folderId',
            lazy: async () => {
              const { SlidespaceSlugPage } = await import('./pages/Slidespaces/components/SlidespaceSlugPage');
              return { Component: SlidespaceSlugPage };
            },
          },
        ],
      },
      {
        path: routesNames.createGroup,
        element: <ProtectedRoute element={<GroupEditView />} />,
        errorElement: <RouteErrorPageView />,
      },
      {
        path: routesNames.editGroup(':groupId'),
        element: <ProtectedRoute element={<GroupEditView />} />,
        errorElement: <RouteErrorPageView />,
      },
      {
        path: routesNames.assets(':slug'),
        element: <ProtectedRoute element={<AssetsManagerView />} />,
        errorElement: <RouteErrorPageView />,
      },
      {
        path: routesNames.folders(':slug', ':folderId'),
        element: <ProtectedRoute element={<AssetsManagerView />} />,
        errorElement: <RouteErrorPageView />,
      },
      {
        path: routesNames.createFolder(':slug', ':folderId'),
        element: <ProtectedRoute element={<FolderFormView />} />,
        errorElement: <RouteErrorPageView />,
      },
      {
        path: routesNames.entityEdit(':slug', ':entityType', ':entityId?'),
        element: <ProtectedRoute element={<EntityEditView />} />,
        errorElement: <RouteErrorPageView />,
      },
      {
        path: routesNames.editProject,
        element: <ProtectedRoute element={<ProjectMainPage />} />,
        errorElement: <RouteErrorPageView />,
      },
    ],
  },
]);

export const Routes = () => <RouterProvider router={router} />;
